import React, { createRef, Component } from 'react';
import { Unsubscribe } from 'redux';

import {
	default_layout,
	layoutByWindowWidth,
	Layout,
} from '@bit/bit.rendering.constants.layouts';
import FrameCommunicator from '@bit/bit.capsule.iframe.communicator';

import { toParentModel, ParentModel, mapLegacyCapsuleTypes } from './parent-adapter';
import { generateDefaultExample, calcMainFile } from '../../state/default-code-generator';
import { IndexedFiles, PlaygroundFs } from '../../state/files-store';
import { IExample } from '../../state/example-type';

import { Playground, PlaygroundConfig } from '../playground';

type BitDevPlaygroundState = {
	config: PlaygroundConfig;
	example?: IExample;
	fs?: PlaygroundFs;
};

//AKA Playground with Parental Controls 👨‍👧
export class BitDevPlayground extends Component<{}, BitDevPlaygroundState> {
	private parent?: FrameCommunicator;

	private playgroundRef = createRef<Playground>();
	private isSubscribedToModel = false; //outside of state, as it not needed in render

	constructor(props: {}) {
		super(props);

		this.state = {
			config: {
				layout: layoutByWindowWidth() || default_layout,
				canWrite: true,
			},
			example: undefined,
		};
	}

	componentDidMount() {
		this.parent = new FrameCommunicator({
			selfFrameId: window.name,
			targetFrameId: undefined,
			isDebugging: false,
		});

		this.registerParentEvents();

		//inform parent that playground is available:
		this.parent.sendMessage('didMount', {});
	}

	private registerParentEvents = () => {
		const { parent } = this;
		if (!parent) return;

		parent.onMessage('updateConf', this.handleUpdateConf);
		parent.onMessage('setModel', this.handleSetModel);
		parent.onMessage(
			'triggerReload',
			() => this.playgroundRef.current && this.playgroundRef.current.reloadPreview()
		);
		parent.onMessage('subscribeToModel', () => (this.isSubscribedToModel = true));
		parent.onMessage('unsubscribeToModel', () => (this.isSubscribedToModel = false));
	};

	//consider using io-ts for live typechecking
	handleUpdateConf = (conf: { canWrite?: boolean; layout?: Layout } = {}) => {
		const { canWrite, layout } = conf;

		const nextConfig = { ...this.state.config };

		if (canWrite === true || canWrite === false) {
			nextConfig.canWrite = canWrite;
		}

		if (!!layout) {
			nextConfig.layout = layout;
		}

		this.setState({
			config: nextConfig,
		});
	};

	//this method is outside the React lifecycle!
	//use setState only once, or use unstable_batchedUpdates()
	handleSetModel = (update: ParentModel) => {
		const { targetComponent, staticUrl, renderer, mainFile, files, id } = update;
		const canonicalRenderer = mapLegacyCapsuleTypes(renderer);

		if (files === undefined)
			throw new TypeError(
				'setModel: expected files to be defined. Set a value, or use NULL to request default example.'
			);

		let defaultExample = undefined;
		if (files === null) {
			defaultExample = generateDefaultExample({
				capsuleType: canonicalRenderer,
				targetComponent,
			});

			const { parent } = this;
			parent && parent.sendMessage('modelDidChange', defaultExample);
		}

		const nextMainFile = calcMainFile(renderer) || mainFile; //@hack
		const nextFiles = files || (defaultExample && defaultExample.files);

		const nextExampleState = {
			renderer: canonicalRenderer,
			targetComponent: targetComponent,
			staticUrl,
			codeId: id,
			mainFile: nextMainFile,
		};

		this.setState({
			example: nextExampleState,
			fs: this.makeFs(nextFiles, nextMainFile),
		});
	};

	private fsUnsubscribe?: Unsubscribe;
	makeFs = (files?: IndexedFiles, mainFile?: string) => {
		const playgroundFs = new PlaygroundFs(files);

		//@HACK - mainFile should always be available
		if (mainFile && files && !files[mainFile]) {
			playgroundFs.writeFileSync(mainFile, '');
		}

		this.fsUnsubscribe && this.fsUnsubscribe();
		this.fsUnsubscribe = playgroundFs.subscribe(() =>
			this.handlePlaygroundFsChanged(playgroundFs)
		);

		return playgroundFs;
	};

	private handlePlaygroundFsChanged = (fs: PlaygroundFs) => {
		const { parent } = this;
		const example = this.state.example;

		if (!parent || !example) return;
		const { mainFile = 'index.js' } = example;

		if (this.isSubscribedToModel) {
			const parentModel = toParentModel(fs, mainFile);
			parent.sendMessage('modelDidChange', parentModel);
		}
	};

	render() {
		const { example, config, fs } = this.state;

		return (
			<Playground
				ref={this.playgroundRef}
				example={example}
				config={config}
				overrideFs={fs}
			/>
		);
	}
}
